/**
 * COMPONENT_SPEC §4 — cloth. OWNER: web-frontend-engineer.
 *
 * L-A: no component here draws a border, fill, shadow or hover ground on an
 * element containing cloth. The card has no hover ground at all — a fill under
 * a swatch would shift the surround lightness beneath the cursor and change the
 * perceived colour of the cloth the client is judging.
 *
 * The image assets are the fabric specialist's: a RENDERED cloth is a PNG under
 * `public/fabric/**`; a PHOTOGRAPHED cloth is the committed supplier file under
 * `/fabric-real/**`, served by reference with a crop rectangle in its record
 * (D-072). `ClothImage` below applies that crop. Where a capture is absent this
 * renders `ST-BOOK-IMAGE-MISSING` — a neutral tile at the correct dimensions
 * stating the capture is not yet made. Never a broken image, never a grey box,
 * never a stock substitute, and never a blur-up (a blurred placeholder states a
 * colour that belongs to no cloth).
 */

import type { CSSProperties } from 'react';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import { swatchImage } from '@pt/fabric';
import type { ClothSummary } from '../lib/contracts.ts';
import { compositionLine, metres, money } from '@pt/view';
import { patternLabel, weaveLabel } from '../lib/label.ts';
import { occasionLabel } from '../lib/optionCopy.ts';
import {
  BOOK_PAGE_SIZE,
  visibleShelves,
  type BookPage,
  type BookShelf,
} from '../lib/shelves.ts';
import { Button, FactLine, Link, RovingRadioGroup, Statement } from './primitives.tsx';

/* ------------------------------------------------------- AvailabilityLine */

export interface AvailabilityLineProps {
  readonly availableMetres: number;
  readonly requirementMetres: number | null;
  readonly commissionTypeLabel: string | null;
  readonly retired?: boolean;
}

/**
 * `EL-AVAILABILITY`. One field, every band, same slot, same size, same weight,
 * same colour. Only the digits differ. No badge, pill, icon, border, fill,
 * alert colour or motion exists for it to acquire.
 */
export function availabilityText({
  availableMetres,
  requirementMetres,
  commissionTypeLabel,
  retired = false,
}: AvailabilityLineProps): string {
  if (retired) return t('el.availability.retired');
  const inContext = requirementMetres != null && commissionTypeLabel != null;
  if (availableMetres <= 0) {
    return inContext
      ? t('el.availability.none.inContext', {
          commissionTypeLabel,
          requirementMetres: metres(requirementMetres),
        })
      : t('el.availability.none.outOfContext');
  }
  return inContext
    ? t('el.availability.inContext', {
        availableMetres: metres(availableMetres),
        commissionTypeLabel,
        requirementMetres: metres(requirementMetres),
      })
    : t('el.availability.outOfContext', { availableMetres: metres(availableMetres) });
}

export function AvailabilityLine(props: AvailabilityLineProps) {
  return <FactLine>{availabilityText(props)}</FactLine>;
}

/* --------------------------------------------------------- ColourStatement */

/**
 * SCALE TRUTH, in one place. Measured on the live book (6 September 2026, the
 * STYLIST-2 screenshot): "Navy Stripe 1083-0906 — Plain — no repeat". Every one
 * of the book's 938 patterned cloths has no repeat figure on file, and the line
 * read the missing figure as "plain". A stripe with no figure is a stripe whose
 * repeat is not on file — that is the sentence now. The pattern word is the
 * catalogue's (`patternLabel`); where the catalogue has none the honest
 * fallback is the plain line only for a plain, and the repeat sentence's
 * neutral form otherwise.
 */
export function scaleText(pattern: string | undefined, patternRepeatCm: number | null): string {
  if (patternRepeatCm != null) return t('el.colour.scale.repeat', { repeatCm: metres(patternRepeatCm) });
  const key = pattern ?? 'solid';
  if (key === 'solid' || key === 'plain') return t('el.colour.scale.plain');
  const word = patternLabel(key);
  return word === null ? t('el.colour.scale.unmeasured.unnamed') : t('el.colour.scale.unmeasured', { pattern: word });
}

export interface ColourStatementProps {
  readonly clothName: string;
  /** the catalogue's pattern key; absent reads as plain (the older callers) */
  readonly pattern?: string;
  readonly patternRepeatCm: number | null;
  readonly onRequestSwatch?: () => void;
  readonly swatchHref: string;
  /** On a card the statement IS the request; on the cloth's own surface it is a full action. */
  readonly asAction?: boolean;
  /**
   * The generic "indicative · not measured · screen unknown" disclaimer. It is
   * IDENTICAL for every cloth, so on the grid it is stated ONCE at book level
   * (RULING-009, the standing placeholder line) rather than repeated under all
   * 22 tiles (reduction.md Finding 1). The cloth's own surface keeps it — there
   * it is the one place, not a repetition.
   */
  readonly showColourNote?: boolean;
}

/**
 * `EL-COLOUR`. Two claims, NEVER merged — tolerance and provenance — with the
 * remedy adjacent in the same statement. No ΔE figure, no "matched", no "true
 * to life", no "accurate to within", no CSS-drawn ruler. Scale truth is the
 * pattern repeat in centimetres, or "Plain — no repeat".
 */
export function ColourStatement({
  clothName,
  pattern,
  patternRepeatCm,
  onRequestSwatch,
  swatchHref,
  asAction = false,
  showColourNote = true,
}: ColourStatementProps) {
  return (
    <div>
      {showColourNote ? (
        <FactLine label={t('el.colour.label')}>
          {t('el.colour.value.indicative')} {t('el.colour.provenance.notMeasured')}{' '}
          {t('el.colour.screenFact')}
        </FactLine>
      ) : null}
      <FactLine>
        {scaleText(pattern, patternRepeatCm)}
      </FactLine>
      {asAction ? (
        <div className="pt-actions">
          <Button onClick={onRequestSwatch}>{t('el.colour.action')}</Button>
        </div>
      ) : (
        <FactLine>
          <Link
            href={swatchHref}
            onNavigate={() => onRequestSwatch?.()}
            ariaLabel={`${t('el.colour.action')} — ${clothName}`}
          >
            {t('el.colour.action')}
          </Link>
        </FactLine>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- ClothImage */

export interface ClothImageProps {
  readonly clothId: string;
  /** The record's own path. Never a URL this file builds. */
  readonly src: string;
  readonly className: string;
  readonly alt: string;
  readonly lazy?: boolean;
}

/**
 * A cloth image, showing exactly the pixels the record says are the cloth.
 *
 * A photographed cloth is served BY REFERENCE from the committed supplier file
 * (D-072), and that file is a photograph of a swatch ON A CARD: it contains a
 * yellow label chip, a card edge and a piece of somebody's table. The record
 * carries the crop that is the cloth, in source pixels, and this renders THAT
 * — no filter, no correction, no re-encode (VISUAL_SYSTEM §5.4 L5): the same
 * bytes, windowed.
 *
 * The maths, once. `s` is the scale that makes the crop COVER the box:
 *
 *     s          = max(boxW / cropW, boxH / cropH)
 *     img width  = sourceW * s   ->  max(sourceW/cropW * 100cqw,
 *                                        sourceW/cropH * 100cqh)
 *     img origin = box centre, translated back by the crop's own centre,
 *                  as a percentage of the image's own size
 *
 * Container query units are what let one rule cover a square macro band and a
 * portrait card tile without either side hard-coding the other's aspect. The
 * plain-percentage fallback beneath it fills the box's WIDTH, which is right
 * for any box at least as wide as the crop.
 *
 * A cloth with no crop — every rendered swatch — is the file itself, and gets a
 * plain `<img>`: no wrapper, no containment, nothing to go wrong.
 */
export function ClothImage({ clothId, src, className, alt, lazy = false }: ClothImageProps) {
  const spec = swatchImage(clothId);
  const crop = spec?.crop;
  const sourceW = spec?.sourcePixelWidth;
  const sourceH = spec?.sourcePixelHeight;
  const loading = lazy ? ('lazy' as const) : undefined;
  if (!crop || !sourceW || !sourceH) {
    return <img className={className} src={src} alt={alt} loading={loading} />;
  }
  const pct = (v: number): string => `${(Math.round(v * 10_000) / 10_000).toString()}%`;
  const style = {
    '--pt-crop-scale-w': ((sourceW / crop.width) * 100).toFixed(4),
    '--pt-crop-scale-h': ((sourceW / crop.height) * 100).toFixed(4),
    '--pt-crop-cx': pct(((crop.left + crop.width / 2) / sourceW) * 100),
    '--pt-crop-cy': pct(((crop.top + crop.height / 2) / sourceH) * 100),
  } as CSSProperties;
  return (
    <span className={`pt-crop ${className}`} style={style}>
      <img className="pt-crop__image" src={src} alt={alt} loading={loading} />
    </span>
  );
}

/* -------------------------------------------------------------- MacroSwatch */

export interface MacroSwatchProps {
  readonly cloth: ClothSummary;
}

/**
 * The colour-truth surface (D-006). Its surround is a MID-GREY, because
 * critical colour judgement is made against a mid-grey surround; a near-white
 * surround makes a mid-tone cloth read darker and more saturated than it is.
 * The band carries no text. It swaps on a single frame — no fade, ever.
 */
export function MacroSwatch({ cloth }: MacroSwatchProps) {
  const plainish = cloth.pattern === 'solid' || cloth.pattern === 'plain';
  const alt =
    cloth.patternRepeatCm == null
      ? (plainish
        ? t('cloth.macro.aria.plain', {
            clothName: cloth.name,
            weaveLabel: weaveLabel(cloth.weave) ?? '',
          })
        : t('cloth.macro.aria.unmeasured', {
            clothName: cloth.name,
            weaveLabel: weaveLabel(cloth.weave) ?? '',
            pattern: patternLabel(cloth.pattern) ?? t('cloth.pattern.other'),
          }))
      : t('cloth.macro.aria', {
          clothName: cloth.name,
          weaveLabel: weaveLabel(cloth.weave) ?? '',
          repeatCm: metres(cloth.patternRepeatCm),
        });
  return (
    <div className="pt-macro">
      <div className="pt-macro__band">
        {cloth.macroSwatchPath == null ? (
          <div className="pt-macro__missing" role="img" aria-label={alt}>
            {t('book.imageMissing')}
          </div>
        ) : (
          <ClothImage
            clothId={cloth.id}
            src={cloth.macroSwatchPath}
            className="pt-macro__cloth"
            alt={alt}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- SwatchCard */

export interface SwatchCardProps {
  readonly cloth: ClothSummary;
  readonly commissionTypeLabel: string | null;
  readonly href: string;
  readonly onOpen: (clothId: string) => void;
  /** Tier 0 revealed: visibly non-selectable, with its reason, and it still opens. */
  readonly revealedUnavailable?: boolean;
}

/**
 * The cloth IS the card. No border, no fill, no shadow — the image is the
 * boundary. Every field renders on every card; a field states its value, it
 * never disappears. Tier 1 is PIXEL-IDENTICAL to Tier 2 plus one sentence.
 */
export function SwatchCard({
  cloth,
  commissionTypeLabel,
  href,
  onOpen,
  revealedUnavailable = false,
}: SwatchCardProps) {
  const availability = availabilityText({
    availableMetres: cloth.availableMetres,
    requirementMetres: cloth.clothRequirementMetres,
    commissionTypeLabel,
    retired: cloth.lifecycleState === 'discontinued',
  });
  const reason = clothReasonText(cloth);
  return (
    <div>
      <a
        className="pt-card"
        href={href}
        aria-label={t('el.availability.aria', {
          clothName: cloth.name,
          availabilityText: availability,
        })}
        {...(revealedUnavailable ? { 'aria-disabled': true } : {})}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          onOpen(cloth.id);
        }}
      >
        {cloth.macroSwatchPath == null ? (
          <div className="pt-card__missing">{t('book.imageMissing')}</div>
        ) : (
          <ClothImage
            clothId={cloth.id}
            src={cloth.macroSwatchPath}
            className="pt-card__image"
            alt=""
            lazy
          />
        )}
        <span className="pt-card__name">{cloth.name}</span>
      </a>
      <FactLine>
        {t('cloth.facts', {
          composition: compositionLine(cloth.composition),
          weightGsm: cloth.weight_gsm,
          weaveLabel: weaveLabel(cloth.weave) ?? '',
          widthCm: cloth.width_cm,
        })}
      </FactLine>
      <FactLine emphasis>
        {cloth.totalPriceForCommissionMinor != null && commissionTypeLabel != null
          ? t('book.card.priceInContext', {
              priceGBP: money(cloth.totalPriceForCommissionMinor, cloth.currency),
              commissionTypeLabel,
            })
          : t('book.card.priceOutOfContext', {
              pricePerMetreGBP: money(cloth.pricePerMetreMinor, cloth.currency),
            })}
      </FactLine>
      <AvailabilityLine
        availableMetres={cloth.availableMetres}
        requirementMetres={cloth.clothRequirementMetres}
        commissionTypeLabel={commissionTypeLabel}
        retired={cloth.lifecycleState === 'discontinued'}
      />
      {/* The generic colour disclaimer is stated ONCE at book level (RULING-009);
          the tile keeps only what varies — the pattern repeat (reduction.md
          Finding 1). AMENDED 5 September 2026 (C4): the swatch act no longer
          stands on every tile — on the full book that was the same act 1,798
          times. The tile is one act, opening the cloth; the swatch is offered
          on the cloth's own page as a full action (SCR-CLOTH), and the book says
          once, per page, where to go (`book.open.hint`). */}
      <FactLine>{scaleText(cloth.pattern, cloth.patternRepeatCm)}</FactLine>
      {/* Tier 1: ONE factual sentence, the highest-ranked condition. Tier 2 says
          nothing at all — silence is the compliment. */}
      {reason === null ? (
        commissionTypeLabel === null ? (
          <FactLine>
            {t('book.card.forWhat', {
              occasionList: cloth.occasions
                .map((o) => occasionLabel(o))
                .join(', '),
              garmentList: cloth.garments.map((g) => tDynamic(`global.garment.${g}`)).join(', '),
            })}
          </FactLine>
        ) : null
      ) : (
        <FactLine>{reason}</FactLine>
      )}
    </div>
  );
}

/**
 * The server ranks and supplies the reason; this only resolves its key.
 *
 * AMENDED 5 September 2026 (C1). Two things this no longer does:
 *
 *   · A Tier 0 reason is NOT rendered here. The `book.tier0.reason.*` keys are
 *     CLAUSES, written to complete `book.tier0.statement` ("… 3 in the book
 *     can’t — there isn’t enough of them for this commission."). Rendered on
 *     their own they are the orphan fragment a client saw on his cloth page.
 *     A Tier 0 cloth's reason is stated in full where it belongs: by the
 *     book-level statement on the grid, and by `notSelectableStatement` on
 *     the cloth's own surface. Tier 1 keys are whole sentences and render.
 *   · The server's English (`tierReason`) is never shown. It is written for
 *     the engineer reading the payload — "barathea is not the weave…", "…is
 *     not cut into trouser" — and carries raw ids (law 32(b)). A reason
 *     reaches a client only through its copy key, or not at all.
 */
export function clothReasonText(cloth: ClothSummary): string | null {
  if (cloth.tier === 0) return null;
  if (cloth.tierReasonCopyKey != null) return tDynamic(cloth.tierReasonCopyKey);
  return null;
}

/* ------------------------------------------------------------ SwatchBookGrid */

export interface SwatchBookGridProps {
  /** The offered cloths ON THIS PAGE. */
  readonly cloths: readonly ClothSummary[];
  /** The revealed Tier-0 cloths ON THIS PAGE — empty unless revealed. */
  readonly hidden: readonly ClothSummary[];
  readonly revealed: boolean;
  readonly onToggleReveal: () => void;
  readonly commissionTypeLabel: string | null;
  readonly onOpen: (clothId: string) => void;
  readonly hiddenReasonKey: CopyKey;
  /**
   * C4 (5 September 2026): the grid shows one page, so the Tier-0 statement's
   * two figures are given to it whole — the shelf's offered count and its
   * hidden count — rather than read off the page's own rows, which would say
   * "24 cloths can be made" of a shelf of four hundred.
   */
  readonly offeredTotal: number;
  readonly hiddenTotal: number;
}

/**
 * One grid. The book. Rendered identically in `SCR-BOOK` and inside the
 * commission flow as `SCR-CLOTHSTEP` — not a second grid.
 *
 * Revealed Tier 0 cloths are APPENDED at the end, never interleaved and never
 * re-sorted in, so not one already-visible tile moves (V7).
 */
export function SwatchBookGrid({
  cloths,
  hidden,
  revealed,
  onToggleReveal,
  commissionTypeLabel,
  onOpen,
  hiddenReasonKey,
  offeredTotal,
  hiddenTotal,
}: SwatchBookGridProps) {
  return (
    <div>
      {hiddenTotal === 0 ? null : (
        <div>
          <Statement>
            {t('book.tier0.statement', {
              shownCount: offeredTotal,
              commissionTypeLabel: commissionTypeLabel ?? '',
              hiddenCount: hiddenTotal,
              reason: tDynamic(hiddenReasonKey),
            })}
          </Statement>
          <Button onClick={onToggleReveal}>
            {revealed ? t('book.tier0.hide') : t('book.tier0.reveal')}
          </Button>
        </div>
      )}
      <div className="pt-grid">
        {cloths.map((cloth) => (
          <SwatchCard
            key={cloth.id}
            cloth={cloth}
            commissionTypeLabel={commissionTypeLabel}
            href={`/book/${cloth.id}`}
            onOpen={onOpen}
          />
        ))}
      </div>
      {revealed && hidden.length > 0 ? (
        <div className="pt-grid" role="region" aria-live="polite">
          {hidden.map((cloth) => (
            <SwatchCard
              key={cloth.id}
              cloth={cloth}
              commissionTypeLabel={commissionTypeLabel}
              href={`/book/${cloth.id}`}
              onOpen={onOpen}
              revealedUnavailable
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- the shelves */

/**
 * C4, 5 September 2026 (STYLIST-1 fix wave; the ruling is in DESIGN_RULINGS.md
 * under "STYLIST-1 FIX WAVE"). The book leads with its shelves, the way the
 * demo's book does: one row of the house's shelves in the house's order, the
 * whole book last, and only the shelves that hold a cloth under the filters in
 * force (facet values render only when present in the current result set —
 * the chosen shelf stays so the empty sentence can be said about it). Words,
 * never colour chips (M-4); the shelf's word is the catalogue's.
 */
export function BookShelves({
  counts,
  chosen,
  onChoose,
}: {
  readonly counts: Readonly<Record<BookShelf, number>>;
  readonly chosen: BookShelf;
  readonly onChoose: (shelf: BookShelf) => void;
}) {
  return (
    <fieldset className="pt-filter pt-shelves">
      <legend className="pt-filter__legend">{t('book.shelf.legend')}</legend>
      <RovingRadioGroup
        ariaLabel={t('book.shelf.legend')}
        values={visibleShelves(counts, chosen)}
        chosen={chosen}
        labelOf={shelfLabel}
        onChoose={(value) => onChoose(value as BookShelf)}
        rowClassName="pt-filter__values pt-filter__values--row"
      />
    </fieldset>
  );
}

export function shelfLabel(shelf: string): string {
  return tDynamic(`book.shelf.${shelf}`);
}

/** The count line above the page: the figure and what it is a figure OF. */
export function bookCountLine(shelf: BookShelf, count: number): string {
  if (shelf === 'all') return count === 1 ? t('book.all.count.one') : t('book.all.count', { count: String(count) });
  return count === 1 ? t('book.shelf.count.one') : t('book.shelf.count', { count: String(count) });
}

/**
 * The page acts — the range, and only the acts that exist (L-C: nothing
 * disabled). Nothing when the shelf fits on one page. The same construction
 * as the atelier cloth list's pager (C3), with the client book's own words.
 */
export function BookPager<Row>({
  page,
  onPage,
}: {
  readonly page: BookPage<Row>;
  readonly onPage: (page: number) => void;
}) {
  if (page.total <= BOOK_PAGE_SIZE) return null;
  return (
    <div className="pt-actions pt-pager">
      <FactLine>
        {t('book.page.range', {
          fromRow: String(page.fromRow),
          toRow: String(page.toRow),
          totalRows: String(page.total),
        })}
      </FactLine>
      {page.page > 1 ? <Button onClick={() => onPage(page.page - 1)}>{t('book.page.previous')}</Button> : null}
      {page.page < page.pageCount ? (
        <Button onClick={() => onPage(page.page + 1)}>{t('book.page.next')}</Button>
      ) : null}
    </div>
  );
}
